import {
    loadGameRuntime,
    loadManagerRuntimeController
} from './app-lazy-runtime.js';
import { getManagerAvatarSvg, warmManagerAvatarProvider } from './manager/avatar/AvatarService.js';
import { getCampaignSaveRepository } from './persistence/CampaignSaveRepository.js';
import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import type { AppRuntime, AppSnapshot, ManagerVm } from './ui/runtime/app-runtime.js';
import type { MatchUiSnapshotV2 } from './ui/runtime/contracts/match-ui-v2.js';
import { AppHost } from './ui/runtime/app-host.js';
import { navigateTo } from './ui/runtime/router.js';
import type { MatchUiState } from './core/Game.js';
import { computeRoleOverallRating, rankRoleRelevantAttributes } from './manager/role-ratings.js';

const TEAM_NAME_STORAGE_KEY = 'tsm-launch-team-name-v1';
const CAMPAIGN_STORE_SLOT = 'default';
const DEFAULT_TEAM_NAME = 'Managed United';
const MANAGED_CLUB_ID = 'club_0';
const SPA_RESTORE_PATH_KEY = 'tsm-spa-restore-path';
const VITE_PRELOAD_RETRY_KEY = 'tsm-vite-preload-retry-v1';

const appRoot = document.getElementById('app-root');
const loading = document.getElementById('loading');
const gameContainer = document.getElementById('game-container');

const speedOptions = [1, 2, 4, 8, 16, 32] as const;

function computePlayerOverallRating(player: any): number {
    const role = String(player?.role ?? player?.position ?? '').toUpperCase();
    const attrs = player?.attributes ?? {};
    if (attrs && Object.keys(attrs).length > 0) {
        return computeRoleOverallRating(role, attrs);
    }
    if (typeof player?.rating === 'number' && Number.isFinite(player.rating)) {
        return Math.max(0, Math.min(100, Math.round(player.rating)));
    }
    return 50;
}

const TRANSFER_ATTRIBUTE_KEYS = [
    'pace',
    'ballControl',
    'passing',
    'tackling',
    'endurance',
    'morale',
    'positioning',
    'shotPower',
    'jumping',
    'reactions',
    'composure',
    'goalkeeping'
] as const;

type TransferAttributeKey = typeof TRANSFER_ATTRIBUTE_KEYS[number];

function toAttributeScore(value: unknown, fallback: number): number {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return fallback;
    }
    return Math.max(1, Math.min(99, Math.round(numeric)));
}

function mapTransferAttributes(target: any): Record<TransferAttributeKey, number> {
    const role = String(target?.position ?? target?.role ?? '').toUpperCase();
    const rating = Number.isFinite(target?.rating) ? Number(target.rating) : 60;
    const baseGoalkeeping = role === 'GK' ? Math.max(45, Math.min(95, Math.round(rating + 8))) : 30;
    return {
        pace: toAttributeScore(target?.attributes?.pace, rating),
        ballControl: toAttributeScore(target?.attributes?.ballControl, rating),
        passing: toAttributeScore(target?.attributes?.passing, rating),
        tackling: toAttributeScore(target?.attributes?.tackling, rating),
        endurance: toAttributeScore(target?.attributes?.endurance, rating),
        morale: toAttributeScore(target?.attributes?.morale, 65),
        positioning: toAttributeScore(target?.attributes?.positioning, rating),
        shotPower: toAttributeScore(target?.attributes?.shotPower, rating),
        jumping: toAttributeScore(target?.attributes?.jumping, rating),
        reactions: toAttributeScore(target?.attributes?.reactions, rating),
        composure: toAttributeScore(target?.attributes?.composure, rating),
        goalkeeping: toAttributeScore(target?.attributes?.goalkeeping, baseGoalkeeping)
    };
}

function estimateScoutQuality(staff: any[]): number {
    const scouts = (staff ?? []).filter((entry) => String(entry?.role ?? '').toLowerCase() === 'scout');
    if (!scouts.length) {
        return 50;
    }
    const sum = scouts.reduce((total: number, scout: any) => total + (Number.isFinite(scout?.quality) ? Number(scout.quality) : 50), 0);
    return Math.max(1, Math.min(99, Math.round(sum / scouts.length)));
}

function mapTransferScoutUncertainty(target: any, scoutQuality: number): Record<TransferAttributeKey, number> {
    const targetConfidence = Number.isFinite(target?.scoutConfidence) ? Number(target.scoutConfidence) : 50;
    const effectiveConfidence = Math.max(1, Math.min(99, Math.round((scoutQuality * 0.65) + (targetConfidence * 0.35))));
    const defaultMargin = Math.max(2, Math.min(14, Math.round(14 - (effectiveConfidence / 8))));
    const qualityScale = Math.max(0.6, Math.min(1.4, (105 - scoutQuality) / 70));
    const mapped = {} as Record<TransferAttributeKey, number>;
    for (const key of TRANSFER_ATTRIBUTE_KEYS) {
        const source = Number(target?.scoutUncertaintyByAttribute?.[key]);
        const baseline = Number.isFinite(source) ? source : defaultMargin;
        mapped[key] = Math.max(1, Math.min(20, Math.round(baseline * qualityScale)));
    }
    return mapped;
}

type GameInstance = {
    gameState: string;
    init: () => Promise<void>;
    initManagerRuntime: () => Promise<void>;
    initMatchRuntime: () => Promise<void>;
    startMatch: () => void;
    stop: () => void;
    setMatchFinishedCallback: (callback: (payload?: any) => void) => void;
    setPerformanceProfile: (profile: string) => void;
    setActiveViewMode: (mode: 'match_visible' | 'match_hidden' | 'manager' | 'splash') => void;
    togglePause: () => boolean;
    isPaused: () => boolean;
    setSimulationSpeed: (speed: 1 | 2 | 4 | 8 | 16 | 32) => void;
    getSimulationSpeed: () => 1 | 2 | 4 | 8 | 16 | 32;
    enableAudio: () => Promise<void>;
    setAudioMix: (mix: { muted?: boolean }) => void;
    getAudioMix: () => { muted?: boolean };
    setCameraMode: (mode: string) => string;
    getCameraMode: () => string;
    setHomeFormation: (formation: string) => void;
    setHomeTactics: (tactics: Partial<{ passLength: number; mentality: number; pressing: number; possession: number }>) => void;
    getHomeFormation: () => string;
    getHomeTactics: () => { passLength: number; mentality: number; pressing: number; possession: number };
    getManagedSubstitutionState: () => {
        used: number;
        max: number;
        bench: Array<{ id: number; name: string; role: string }>;
        onField: Array<{ id: number; name: string; role: string }>;
        canSubNow: boolean;
    };
    makeManagedSubstitution: (outPlayerId: number, inPlayerId: number) => { ok: boolean; reason: string };
    toggleHomePlayerHighlight: (playerId: number) => void;
    toggleHomePlayerDetails: (playerId: number) => void;
    getMatchUiState: () => MatchUiState;
    setMatchUiListener: (listener: ((state: MatchUiState) => void) | null) => void;
    flushCampaignSave?: () => Promise<void>;
};

type ManagerController = {
    init: () => void;
    getState: () => any;
    getHomeSummary: () => any;
    getSquadState: () => any;
    getFixturesForScreen: () => any[];
    getInboxState: () => { items: any[] };
    getStaffState: () => { staff: any[]; finance: any };
    getTransferState: () => {
        market: any[];
        shortlist: any[];
        listedPlayers?: any[];
        incomingOffers?: any[];
        negotiations?: any[];
        finance?: { budget?: number; wagesPerWeek?: number };
    };
    getClubName: (clubId: string) => string;
    getInboxBadgeCount: () => number;
    resolveInboxItem: (
        itemId: string,
        optionId: string,
        inputValues?: { bidAmount?: number; wageOffer?: number; listingPrice?: number }
    ) => { ok: boolean; reason: string };
    fireStaff: (staffId: string) => { ok: boolean; reason: string };
    shortlistTransferTarget: (targetId: string) => { ok: boolean; reason: string };
    expressInterestInTarget: (targetId: string) => { ok: boolean; reason: string };
    finalizeTransferDeal: (negotiationId: string) => { ok: boolean; reason: string };
    withdrawTransferNegotiation: (negotiationId: string) => { ok: boolean; reason: string };
    listPlayerForTransfer: (playerId: number, targetPrice: number) => { ok: boolean; reason: string };
    removePlayerFromTransferList: (playerId: number) => { ok: boolean; reason: string };
    acceptIncomingTransferOffer: (offerId: string) => { ok: boolean; reason: string };
    rejectIncomingTransferOffer: (offerId: string) => { ok: boolean; reason: string };
    counterIncomingTransferOffer: (offerId: string, requestedFee: number) => { ok: boolean; reason: string };
    setTrainingPlan: (playerId: number, plan: string) => void;
    setTrainingIntensity: (playerId: number, intensity: number) => void;
    setManagedFormation: (formationName: string) => void;
    swapSelectionSlots: (source: { group: 'xi' | 'bench' | 'out'; index: number }, target: { group: 'xi' | 'bench' | 'out'; index: number }) => void;
    moveSelectionPlayer?: (source: any, target: any) => boolean;
    getTeamSelectionViewModel: () => any;
    getManagedMatchIntroSummary: () => {
        fixtureId: string;
        homeClubName: string;
        awayClubName: string;
        date: string;
        location: string;
        competition: string;
    } | null;
    getLastManagedMatchResult: () => {
        fixtureId: string;
        homeClubName: string;
        awayClubName: string;
        homeGoals: number;
        awayGoals: number;
        goalEvents: Array<{ minute: number; teamId: number; scorerName: string }>;
    } | null;
    getPendingDaySummary: () => {
        date: string;
        headline: string;
        highlights: string[];
        simulatedFixtures: Array<{ homeClubName: string; awayClubName: string; scoreline: string }>;
    } | null;
    setScreen: (screen: 'home' | 'inbox' | 'squad' | 'fixtures' | 'staff' | 'transfers') => void;
    advanceDay: () => { ok: boolean; reason: string };
    openManagedMatchIntro: () => { ok: boolean; reason: string };
    closeManagedMatchIntro: () => void;
    autoFillSelection: () => void;
    beginManagedMatch: () => { ok: boolean; reason: string };
    autoPlayManagedFixture: () => { ok: boolean; reason: string };
    clearLastManagedMatchResult: () => void;
    clearPendingDaySummary: () => void;
    markInboxItemRead: (id: string) => { ok: boolean; reason: string };
    hireStaff: (role: string) => { ok: boolean; reason: string };
    prepareTransferBid: (targetId: string) => { ok: boolean; reason: string; target: any; suggestedBid: number; suggestedWage: number };
    submitTransferBid: (targetId: string, bidAmount: number, wage: number) => { ok: boolean; reason: string };
    enterManagerMode: () => void;
    enterMatchMode: () => void;
};

function sanitizeTeamName(raw: string): { ok: boolean; value: string; error?: string } {
    const collapsed = raw.replace(/\s+/g, ' ').trim();
    const candidate = collapsed || DEFAULT_TEAM_NAME;
    if (candidate.length > 24) {
        return { ok: false, value: candidate, error: 'Team name must be 24 characters or fewer.' };
    }
    if (!/^[A-Za-z ]+$/.test(candidate)) {
        return { ok: false, value: candidate, error: 'Use alphabet letters and spaces only.' };
    }
    return { ok: true, value: candidate };
}

function persistTeamName(teamName: string): void {
    window.localStorage.setItem(TEAM_NAME_STORAGE_KEY, teamName);
}

function loadPersistedTeamName(): string {
    const raw = window.localStorage.getItem(TEAM_NAME_STORAGE_KEY);
    const normalized = sanitizeTeamName(raw ?? '');
    return normalized.ok ? normalized.value : DEFAULT_TEAM_NAME;
}

function readRestorePathFromSession(): string | null {
    const pathValue = window.sessionStorage.getItem(SPA_RESTORE_PATH_KEY);
    if (!pathValue) {
        return null;
    }
    window.sessionStorage.removeItem(SPA_RESTORE_PATH_KEY);
    return pathValue;
}

function formatLabel(value: string): string {
    return String(value).replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

const managerDateFormatter = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
});

function formatManagerDate(value: string): string {
    if (!value) {
        return '--';
    }
    const safeValue = String(value).length <= 10 ? `${value}T12:00:00.000Z` : value;
    const parsed = new Date(safeValue);
    return Number.isNaN(parsed.getTime()) ? String(value) : managerDateFormatter.format(parsed);
}

function formatAttributeLabel(value: string): string {
    return String(value).replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase());
}

class ShellRuntime implements AppRuntime {
    private game: GameInstance | null = null;
    private manager: ManagerController | null = null;
    private ManagerRuntimeControllerCtor: any = null;
    private listeners = new Set<(snapshot: AppSnapshot) => void>();
    private mode: 'splash' | 'manager' | 'match' = 'splash';
    private launchInProgress = false;
    private campaignSaveAvailable = false;
    private campaignManagedTeamName: string | null = null;
    private message = '';
    private selectedSquadPlayerId: number | null = null;
    private focusedTransferTargetId: string | null = null;
    private focusedTransferVersion = 0;
    private matchSnapshot: MatchUiSnapshotV2 = {
        version: 2,
        ready: false,
        isFullTime: false,
        scoreboard: {
            homeTeamName: 'HOME',
            awayTeamName: 'AWAY',
            homeScore: 0,
            awayScore: 0,
            timeText: '00:00',
            phaseText: 'Pre Match',
            carrierText: 'Pre Match',
            cardSummary: '',
            seasonStatus: '',
            seasonTable: '',
            fps: 0
        },
        controls: {
            canStart: true,
            paused: false,
            speed: 1,
            muted: false,
            cameraMode: 'follow'
        },
        tactics: {
            formation: '4-4-2',
            passLength: 2,
            mentality: 2,
            pressing: 2,
            possession: 2
        },
        substitutions: {
            used: 0,
            max: 3,
            canSubNow: false,
            onField: [],
            bench: []
        },
        stats: {
            possession: { home: 50, away: 50 },
            territory: { home: 50, away: 50 },
            shots: { home: 0, away: 0 },
            tacklesWon: { home: 0, away: 0 },
            corners: { home: 0, away: 0 },
            finalThirdEntries: { home: 0, away: 0 }
        },
        managedPlayersTitle: 'Players',
        managedPlayers: []
    };
    private matchListeners = new Set<(snapshot: MatchUiSnapshotV2) => void>();

    private snapshot: AppSnapshot = {
        mode: 'splash',
        splash: {
            teamName: loadPersistedTeamName(),
            hint: 'Use letters and spaces only (max 24 chars).',
            busy: false,
            canLoadCampaign: false
        },
        manager: null
    };

    subscribe(listener: (snapshot: AppSnapshot) => void): () => void {
        this.listeners.add(listener);
        listener(this.snapshot);
        return () => {
            this.listeners.delete(listener);
        };
    }

    private notify(): void {
        for (const listener of this.listeners) {
            listener(this.snapshot);
        }
    }

    private notifyMatch(): void {
        for (const listener of this.matchListeners) {
            listener(this.matchSnapshot);
        }
    }

    private syncMatchSnapshotFromGame(): void {
        if (!this.game) {
            this.matchSnapshot = {
                ...this.matchSnapshot,
                ready: false,
                isFullTime: false
            };
            this.notifyMatch();
            return;
        }
        const state = this.game.getMatchUiState();
        this.matchSnapshot = {
            version: 2,
            ready: true,
            isFullTime: state.isFullTime,
            scoreboard: {
                homeTeamName: state.homeTeamName,
                awayTeamName: state.awayTeamName,
                homeScore: state.score.home,
                awayScore: state.score.away,
                timeText: state.timeText,
                phaseText: state.phaseText,
                carrierText: state.carrierText,
                cardSummary: state.cardSummary,
                seasonStatus: state.seasonStatus,
                seasonTable: state.seasonTable,
                fps: state.fps
            },
            controls: state.controls,
            tactics: state.tactics,
            substitutions: state.substitutions,
            stats: state.stats,
            managedPlayersTitle: state.managedPlayersTitle,
            managedPlayers: state.managedPlayers
        };
        this.notifyMatch();
    }

    private setAppMode(mode: 'splash' | 'manager' | 'match'): void {
        this.mode = mode;
        this.snapshot = { ...this.snapshot, mode };
        const managerActive = mode === 'manager';
        document.body.classList.toggle('manager-ui-active', managerActive);
        document.documentElement.classList.toggle('manager-ui-active', managerActive);
        if (mode === 'match') {
            if (appRoot) {
                appRoot.style.display = 'block';
            }
            if (gameContainer) {
                gameContainer.style.display = 'block';
            }
            this.manager?.enterMatchMode();
        } else {
            if (appRoot) {
                appRoot.style.display = 'block';
            }
            if (gameContainer) {
                gameContainer.style.display = 'none';
            }
            if (mode === 'manager') {
                this.manager?.enterManagerMode();
            }
        }
        this.syncGameViewMode();
        this.notify();
    }

    private syncGameViewMode(): void {
        if (!this.game) {
            return;
        }
        if (document.hidden) {
            this.game.setActiveViewMode('match_hidden');
            return;
        }
        if (this.mode === 'splash') {
            this.game.setActiveViewMode('splash');
            return;
        }
        if (this.mode === 'manager') {
            this.game.setActiveViewMode('manager');
            return;
        }
        this.game.setActiveViewMode('match_visible');
    }

    private async ensureManagerControllerCtor(): Promise<void> {
        if (this.ManagerRuntimeControllerCtor) {
            return;
        }
        const module = await loadManagerRuntimeController();
        this.ManagerRuntimeControllerCtor = module.ManagerRuntimeController;
    }

    private async refreshCampaignSaveState(): Promise<void> {
        const repository = getCampaignSaveRepository();
        const save = await repository.loadCampaign(CAMPAIGN_STORE_SLOT);
        this.campaignSaveAvailable = Boolean(save);
        if (!save || !Array.isArray(save.seasonState?.clubs)) {
            this.campaignManagedTeamName = null;
        } else {
            const managedClub = save.seasonState.clubs.find((club: any) => club?.id === MANAGED_CLUB_ID);
            const normalized = sanitizeTeamName(managedClub?.name ?? '');
            this.campaignManagedTeamName = normalized.ok ? normalized.value : null;
        }
        this.snapshot = {
            ...this.snapshot,
            splash: {
                ...this.snapshot.splash,
                canLoadCampaign: this.campaignSaveAvailable
            }
        };
        this.notify();
    }

    private teardownActiveRun(): void {
        if (this.game) {
            try {
                this.game.setMatchUiListener(null);
                this.game.stop();
            } catch (_error) {
                // Best effort.
            }
        }
        this.game = null;
        this.manager = null;
        this.selectedSquadPlayerId = null;
        this.matchSnapshot.ready = false;
    }

    private async launchQuickGame(teamName: string): Promise<void> {
        this.launchInProgress = true;
        this.snapshot = {
            ...this.snapshot,
            splash: {
                ...this.snapshot.splash,
                busy: true
            }
        };
        this.notify();

        this.teardownActiveRun();
        if (loading) {
            loading.style.display = 'block';
        }
        if (gameContainer) {
            gameContainer.style.display = 'block';
        }

        (globalThis as any).__TSM_SEASON_MODE__ = false;
        (globalThis as any).__TSM_FAST_MATCH_SIM__ = true;
        (globalThis as any).__TSM_SEASON_STORE_SLOT__ = CAMPAIGN_STORE_SLOT;
        (globalThis as any).__TSM_MANAGED_TEAM_NAME__ = teamName;
        try {
            const { Game } = await loadGameRuntime();
            const game = new Game() as GameInstance;
            this.game = game;
            await game.init();
            game.setPerformanceProfile('aggressive');
            game.setMatchUiListener(() => {
                this.syncMatchSnapshotFromGame();
            });
            game.setMatchFinishedCallback(() => {
                this.teardownActiveRun();
                this.setAppMode('splash');
            });
            this.syncMatchSnapshotFromGame();
            if (loading) {
                loading.style.display = 'none';
            }
            this.setAppMode('match');
            try {
                await game.enableAudio();
            } catch (_error) {
                // Browser may block; continue.
            }
            game.startMatch();
        } catch (error) {
            console.error('Failed to launch quick game runtime:', error);
            this.teardownActiveRun();
            this.setAppMode('splash');
            this.snapshot = {
                ...this.snapshot,
                splash: {
                    ...this.snapshot.splash,
                    hint: 'Failed to load match runtime. Please refresh and try again.'
                }
            };
        } finally {
            this.launchInProgress = false;
            this.snapshot = {
                ...this.snapshot,
                splash: {
                    ...this.snapshot.splash,
                    busy: false
                }
            };
            this.notify();
        }
    }

    private async launchCampaign(teamName: string): Promise<void> {
        this.launchInProgress = true;
        this.snapshot = {
            ...this.snapshot,
            splash: {
                ...this.snapshot.splash,
                busy: true
            }
        };
        this.notify();

        this.teardownActiveRun();
        if (loading) {
            loading.style.display = 'block';
        }
        if (gameContainer) {
            gameContainer.style.display = 'block';
        }

        (globalThis as any).__TSM_SEASON_MODE__ = true;
        (globalThis as any).__TSM_FAST_MATCH_SIM__ = true;
        (globalThis as any).__TSM_SEASON_STORE_SLOT__ = CAMPAIGN_STORE_SLOT;
        (globalThis as any).__TSM_MANAGED_TEAM_NAME__ = teamName;
        try {
            await this.ensureManagerControllerCtor();
            const { Game } = await loadGameRuntime();
            const game = new Game() as GameInstance;
            this.game = game;
            await game.initManagerRuntime();
            game.setPerformanceProfile('aggressive');
            game.setMatchUiListener(() => {
                this.syncMatchSnapshotFromGame();
            });
            const ManagerRuntimeController = this.ManagerRuntimeControllerCtor!;
            const manager = new ManagerRuntimeController(game) as ManagerController;
            this.manager = manager;
            manager.init();
            await game.flushCampaignSave?.();
            this.syncMatchSnapshotFromGame();
            if (loading) {
                loading.style.display = 'none';
            }
            this.rebuildManagerSnapshot();
            void warmManagerAvatarProvider().then(() => {
                if (this.manager && this.mode === 'manager') {
                    this.rebuildManagerSnapshot();
                }
            });
            this.setAppMode('manager');
            await this.refreshCampaignSaveState();
        } catch (error) {
            console.error('Failed to launch manager runtime:', error);
            this.teardownActiveRun();
            this.setAppMode('splash');
            this.snapshot = {
                ...this.snapshot,
                splash: {
                    ...this.snapshot.splash,
                    hint: 'Failed to load manager runtime. Please refresh and try again.'
                }
            };
        } finally {
            this.launchInProgress = false;
            this.snapshot = {
                ...this.snapshot,
                splash: {
                    ...this.snapshot.splash,
                    busy: false
                }
            };
            this.notify();
        }
    }

    private async ensureCampaignLoaded(teamName: string): Promise<void> {
        if (this.manager && this.game) {
            return;
        }
        await this.launchCampaign(teamName);
    }

    async onRoute(path: string, _params: Record<string, string>): Promise<void> {
        if (path === '/match') {
            if (this.game && this.manager) {
                this.setAppMode('match');
                this.syncMatchSnapshotFromGame();
                return;
            }
            await this.launchQuickGame(this.snapshot.splash.teamName);
            return;
        }
        if (path.startsWith('/manager')) {
            if (!this.manager || !this.game) {
                await this.refreshCampaignSaveState();
                if (!this.campaignSaveAvailable) {
                    this.setAppMode('splash');
                    this.snapshot = {
                        ...this.snapshot,
                        splash: {
                            ...this.snapshot.splash,
                            hint: 'This route requires a campaign save. Start a campaign first.'
                        }
                    };
                    this.notify();
                    return;
                }
                await this.ensureCampaignLoaded(this.campaignManagedTeamName ?? this.snapshot.splash.teamName);
            }
            const screen = path.replace('/manager/', '');
            if (this.manager) {
                if (screen === 'home' || screen === 'competitions') {
                    this.manager.setScreen('home');
                } else if (screen === 'inbox' || screen === 'squad' || screen === 'fixtures' || screen === 'staff' || screen === 'transfers') {
                    this.manager.setScreen(screen);
                }
            }
            this.rebuildManagerSnapshot();
            this.setAppMode('manager');
            return;
        }
        this.setAppMode('splash');
    }

    async splashQuick(teamName: string): Promise<void> {
        const normalized = sanitizeTeamName(teamName);
        if (!normalized.ok) {
            this.snapshot = {
                ...this.snapshot,
                splash: {
                    ...this.snapshot.splash,
                    hint: normalized.error ?? 'Invalid team name.'
                }
            };
            this.notify();
            return;
        }
        persistTeamName(normalized.value);
        this.snapshot = {
            ...this.snapshot,
            splash: {
                ...this.snapshot.splash,
                teamName: normalized.value,
                hint: 'Use letters and spaces only (max 24 chars).'
            }
        };
        this.notify();
        navigateTo('/match');
    }

    async splashLoad(): Promise<void> {
        await this.refreshCampaignSaveState();
        if (!this.campaignSaveAvailable || this.launchInProgress) {
            return;
        }
        const teamName = this.campaignManagedTeamName ?? loadPersistedTeamName();
        persistTeamName(teamName);
        this.snapshot = {
            ...this.snapshot,
            splash: {
                ...this.snapshot.splash,
                teamName,
                hint: 'Using managed team from the campaign save.'
            }
        };
        this.notify();
        await this.launchCampaign(teamName);
        if (this.manager) {
            navigateTo('/manager/home');
        }
    }

    async splashNew(teamName: string): Promise<void> {
        const normalized = sanitizeTeamName(teamName);
        if (!normalized.ok) {
            this.snapshot = {
                ...this.snapshot,
                splash: {
                    ...this.snapshot.splash,
                    hint: normalized.error ?? 'Invalid team name.'
                }
            };
            this.notify();
            return;
        }
        await this.refreshCampaignSaveState();
        if (this.campaignSaveAvailable) {
            const confirmed = window.confirm('Starting a new campaign will replace the current campaign save. Continue?');
            if (!confirmed) {
                return;
            }
        }
        await getCampaignSaveRepository().deleteCampaign(CAMPAIGN_STORE_SLOT);
        this.campaignSaveAvailable = false;
        this.campaignManagedTeamName = null;
        persistTeamName(normalized.value);
        this.snapshot = {
            ...this.snapshot,
            splash: {
                ...this.snapshot.splash,
                teamName: normalized.value,
                hint: 'Use letters and spaces only (max 24 chars).',
                canLoadCampaign: false
            }
        };
        this.notify();
        await this.launchCampaign(normalized.value);
        if (this.manager) {
            navigateTo('/manager/home');
        }
    }

    async managerNextDay(): Promise<void> {
        if (!this.manager) {
            return;
        }
        const summary = this.manager.getHomeSummary();
        if (summary.ready) {
            const result = this.manager.openManagedMatchIntro();
            this.message = result.reason;
        } else {
            const result = this.manager.advanceDay();
            this.message = result.reason;
        }
        this.rebuildManagerSnapshot();
    }

    async managerSetScreen(screen: 'home' | 'inbox' | 'squad' | 'fixtures' | 'staff' | 'transfers'): Promise<void> {
        if (!this.manager) {
            return;
        }
        this.manager.setScreen(screen);
        this.rebuildManagerSnapshot();
    }

    async managerSelectPlayer(playerId: number): Promise<void> {
        this.selectedSquadPlayerId = playerId;
        this.rebuildManagerSnapshot();
    }

    async managerOpenMessage(messageId: string): Promise<void> {
        if (!this.manager) {
            return;
        }
        this.manager.markInboxItemRead(messageId);
        this.rebuildManagerSnapshot();
    }

    async managerHireStaff(role: string): Promise<void> {
        if (!this.manager) {
            return;
        }
        const result = this.manager.hireStaff(role);
        this.message = result.reason;
        this.rebuildManagerSnapshot();
    }

    async managerBidTransfer(targetId: string): Promise<void> {
        if (!this.manager) {
            return;
        }
        const prepared = this.manager.prepareTransferBid(targetId);
        this.message = prepared.reason;
        this.rebuildManagerSnapshot();
    }

    async managerSquadAction(action: string, payload: Record<string, string>): Promise<void> {
        if (!this.manager) {
            return;
        }

        const parseNumber = (value: string | undefined): number | null => {
            if (!value) {
                return null;
            }
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : null;
        };

        if (action === 'set-training-plan') {
            const playerId = parseNumber(payload.playerId);
            const plan = payload.value;
            if (playerId !== null && plan) {
                this.manager.setTrainingPlan(playerId, plan);
            }
            this.rebuildManagerSnapshot();
            return;
        }

        if (action === 'set-training-intensity') {
            const playerId = parseNumber(payload.playerId);
            const value = parseNumber(payload.value);
            if (playerId !== null && value !== null) {
                this.manager.setTrainingIntensity(playerId, value);
            }
            this.rebuildManagerSnapshot();
            return;
        }

        if (action === 'transfer-list') {
            const playerId = parseNumber(payload.transferListPlayer ?? payload.playerId);
            if (playerId === null) {
                this.message = 'Unable to list player: missing player id.';
                this.rebuildManagerSnapshot();
                return;
            }
            const targetPrice = parseNumber(payload.targetPrice);
            if (targetPrice === null) {
                this.message = 'Unable to list player: target price is required.';
                this.rebuildManagerSnapshot();
                return;
            }
            const result = this.manager.listPlayerForTransfer(playerId, targetPrice);
            this.message = result.reason;
            this.rebuildManagerSnapshot();
            return;
        }

        if (action === 'transfer-unlist') {
            const playerId = parseNumber(payload.transferUnlistPlayer ?? payload.playerId);
            if (playerId === null) {
                return;
            }
            const result = this.manager.removePlayerFromTransferList(playerId);
            this.message = result.reason;
            this.rebuildManagerSnapshot();
            return;
        }
    }

    async managerInboxAction(action: string, payload: Record<string, string>): Promise<void> {
        if (!this.manager) {
            return;
        }
        const parseNumber = (value: string | undefined): number | null => {
            if (!value) {
                return null;
            }
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : null;
        };
        if (action === 'resolve') {
            const itemId = payload.inboxResolve;
            const optionId = payload.inboxOption ?? payload.optionId;
            if (!itemId || !optionId) {
                return;
            }
            const bidAmount = parseNumber(payload.bidAmount);
            const wageOffer = parseNumber(payload.wageOffer);
            const listingPrice = parseNumber(payload.listingPrice);
            const result = this.manager.resolveInboxItem(
                itemId,
                optionId,
                (bidAmount !== null || wageOffer !== null || listingPrice !== null)
                    ? { bidAmount: bidAmount ?? undefined, wageOffer: wageOffer ?? undefined, listingPrice: listingPrice ?? undefined }
                    : undefined
            );
            this.message = result.reason;
            this.rebuildManagerSnapshot();
        }
    }

    async managerTransfersAction(_action: string, payload: Record<string, string>): Promise<void> {
        if (!this.manager) {
            return;
        }
        const parseNumber = (value: string | undefined): number | null => {
            if (!value) {
                return null;
            }
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : null;
        };

        const focusTargetId = payload.transferFocusTarget;
        if (focusTargetId) {
            this.focusedTransferTargetId = focusTargetId;
            this.focusedTransferVersion += 1;
            this.manager.setScreen('transfers');
            this.rebuildManagerSnapshot();
            return;
        }
        const shortlistTargetId = payload.transferShortlist;
        if (shortlistTargetId) {
            const result = this.manager.shortlistTransferTarget(shortlistTargetId);
            this.message = result.reason;
            this.rebuildManagerSnapshot();
            return;
        }
        const expressInterestId = payload.transferExpressInterest;
        if (expressInterestId) {
            const result = this.manager.expressInterestInTarget(expressInterestId);
            this.message = result.reason;
            this.rebuildManagerSnapshot();
            return;
        }
        const submitId = payload.transferSubmit;
        if (submitId) {
            const bidAmount = parseNumber(payload.bidAmount);
            const wageOffer = parseNumber(payload.wageOffer);
            if (bidAmount === null || wageOffer === null) {
                this.message = 'Unable to submit terms: bid amount and wage offer are required.';
                this.rebuildManagerSnapshot();
                return;
            }
            const result = this.manager.submitTransferBid(submitId, bidAmount, wageOffer);
            this.message = result.reason;
            this.rebuildManagerSnapshot();
            return;
        }
        const finalizeId = payload.transferFinalize;
        if (finalizeId) {
            const result = this.manager.finalizeTransferDeal(finalizeId);
            this.message = result.reason;
            this.rebuildManagerSnapshot();
            return;
        }
        const withdrawId = payload.transferWithdraw;
        if (withdrawId) {
            const result = this.manager.withdrawTransferNegotiation(withdrawId);
            this.message = result.reason;
            this.rebuildManagerSnapshot();
            return;
        }
        const listPlayerIdRaw = payload.transferListPlayer;
        if (listPlayerIdRaw) {
            const playerId = parseNumber(listPlayerIdRaw);
            if (playerId !== null) {
                const targetPrice = parseNumber(payload.targetPrice);
                if (targetPrice === null) {
                    this.message = 'Unable to list player: target price is required.';
                    this.rebuildManagerSnapshot();
                    return;
                }
                const result = this.manager.listPlayerForTransfer(playerId, targetPrice);
                this.message = result.reason;
                this.rebuildManagerSnapshot();
            } else {
                this.message = 'Unable to list player: missing player id.';
                this.rebuildManagerSnapshot();
            }
            return;
        }
        const unlistPlayerId = payload.transferUnlistPlayer;
        if (unlistPlayerId) {
            const playerId = parseNumber(unlistPlayerId);
            if (playerId !== null) {
                const result = this.manager.removePlayerFromTransferList(playerId);
                this.message = result.reason;
                this.rebuildManagerSnapshot();
            }
            return;
        }
    }

    async managerStaffAction(action: string, payload: Record<string, string>): Promise<void> {
        if (!this.manager) {
            return;
        }
        if (action === 'hire') {
            const role = payload.role ?? 'assistant_manager';
            const result = this.manager.hireStaff(role);
            this.message = result.reason;
            this.rebuildManagerSnapshot();
            return;
        }
        if (action === 'fire') {
            const staffId = payload.staffFire;
            if (!staffId) {
                return;
            }
            const result = this.manager.fireStaff(staffId);
            this.message = result.reason;
            this.rebuildManagerSnapshot();
        }
    }

    async managerIntroAction(action: string, payload: Record<string, string>): Promise<void> {
        if (!this.manager) {
            return;
        }
        if (action === 'set-formation') {
            const formation = payload.formation;
            if (formation) {
                this.manager.setManagedFormation(formation);
                this.message = `Formation set to ${formation}.`;
            }
            this.rebuildManagerSnapshot();
            return;
        }
        if (action === 'swap-slots') {
            const sourceGroup = payload.sourceGroup;
            const targetGroup = payload.targetGroup;
            const sourceIndex = Number(payload.sourceIndex);
            const targetIndex = Number(payload.targetIndex);
            if ((sourceGroup !== 'xi' && sourceGroup !== 'bench' && sourceGroup !== 'out')
                || (targetGroup !== 'xi' && targetGroup !== 'bench' && targetGroup !== 'out')
                || !Number.isFinite(sourceIndex)
                || !Number.isFinite(targetIndex)) {
                return;
            }
            this.manager.swapSelectionSlots(
                { group: sourceGroup, index: Math.max(0, Math.floor(sourceIndex)) },
                { group: targetGroup, index: Math.max(0, Math.floor(targetIndex)) }
            );
            this.message = 'Updated team selection.';
            this.rebuildManagerSnapshot();
        }
    }

    async managerIntroBack(): Promise<void> {
        if (!this.manager) {
            return;
        }
        this.manager.closeManagedMatchIntro();
        this.rebuildManagerSnapshot();
    }

    async managerIntroAuto(): Promise<void> {
        if (!this.manager) {
            return;
        }
        this.manager.autoFillSelection();
        this.message = 'Auto-selected best available XI and bench.';
        this.rebuildManagerSnapshot();
    }

    async managerIntroStart(): Promise<void> {
        if (!this.manager || !this.game) {
            return;
        }
        await this.game.initMatchRuntime();
        await this.game.enableAudio();
        const result = this.manager.beginManagedMatch();
        this.message = result.reason;
        if (result.ok) {
            navigateTo('/match');
            return;
        }
        this.rebuildManagerSnapshot();
    }

    async managerIntroSkip(): Promise<void> {
        if (!this.manager) {
            return;
        }
        const result = this.manager.autoPlayManagedFixture();
        this.message = result.reason;
        this.rebuildManagerSnapshot();
    }

    async managerResultContinue(): Promise<void> {
        if (!this.manager) {
            return;
        }
        this.manager.clearLastManagedMatchResult();
        this.manager.setScreen('home');
        this.message = 'Back in manager hub.';
        this.rebuildManagerSnapshot();
    }

    async managerDaySummaryBack(): Promise<void> {
        if (!this.manager) {
            return;
        }
        this.manager.clearPendingDaySummary();
        this.rebuildManagerSnapshot();
    }

    async managerDaySummaryContinue(): Promise<void> {
        if (!this.manager) {
            return;
        }
        this.manager.clearPendingDaySummary();
        this.rebuildManagerSnapshot();
    }

    async managerBackToHub(): Promise<void> {
        this.setAppMode('manager');
        this.rebuildManagerSnapshot();
    }

    getMatchUiSnapshot(): MatchUiSnapshotV2 {
        return this.matchSnapshot;
    }

    subscribeMatchUi(listener: (snapshot: MatchUiSnapshotV2) => void): () => void {
        this.matchListeners.add(listener);
        listener(this.matchSnapshot);
        return () => {
            this.matchListeners.delete(listener);
        };
    }

    async matchTogglePauseOrStart(): Promise<{ ok: boolean; reason: string }> {
        if (!this.game) {
            return { ok: false, reason: 'Match runtime unavailable.' };
        }
        if (this.game.gameState === 'PRE_MATCH' || this.game.gameState === 'KICKOFF') {
            await this.game.enableAudio();
            this.game.startMatch();
            return { ok: true, reason: 'Match started.' };
        }
        const paused = this.game.togglePause();
        return { ok: true, reason: paused ? 'Match paused.' : 'Match resumed.' };
    }

    async matchCycleSpeed(): Promise<{ ok: boolean; reason: string; speed?: 1 | 2 | 4 | 8 | 16 | 32 }> {
        if (!this.game) {
            return { ok: false, reason: 'Match runtime unavailable.' };
        }
        const current = this.game.getSimulationSpeed();
        const next = speedOptions[(speedOptions.indexOf(current) + 1) % speedOptions.length];
        this.game.setSimulationSpeed(next);
        return { ok: true, reason: `Simulation speed x${next}.`, speed: next };
    }

    async matchToggleAudio(): Promise<{ ok: boolean; reason: string; muted?: boolean }> {
        if (!this.game) {
            return { ok: false, reason: 'Match runtime unavailable.' };
        }
        const currentMix = this.game.getAudioMix?.() ?? { muted: false };
        const nextMuted = !Boolean(currentMix.muted);
        if (!nextMuted) {
            try {
                await this.game.enableAudio();
            } catch (_error) {
                // Browser permissions may block audio unlock in this tick.
            }
        }
        this.game.setAudioMix({ muted: nextMuted });
        return { ok: true, reason: nextMuted ? 'Audio muted.' : 'Audio unmuted.', muted: nextMuted };
    }

    async matchCycleCamera(): Promise<{ ok: boolean; reason: string; cameraMode?: string }> {
        if (!this.game) {
            return { ok: false, reason: 'Match runtime unavailable.' };
        }
        const modes = ['follow', 'referee', 'broadcast', 'top'];
        const current = this.game.getCameraMode();
        const nextMode = modes[(modes.indexOf(current) + 1) % modes.length];
        const resolved = this.game.setCameraMode(nextMode);
        return { ok: true, reason: `Camera mode: ${resolved}.`, cameraMode: resolved };
    }

    async matchSetFormation(formation: string): Promise<{ ok: boolean; reason: string }> {
        if (!this.game) {
            return { ok: false, reason: 'Match runtime unavailable.' };
        }
        this.game.setHomeFormation(formation);
        return { ok: true, reason: `Formation set to ${formation}.` };
    }

    async matchSetTactic(
        name: 'passLength' | 'mentality' | 'pressing' | 'possession',
        value: number
    ): Promise<{ ok: boolean; reason: string }> {
        if (!this.game) {
            return { ok: false, reason: 'Match runtime unavailable.' };
        }
        this.game.setHomeTactics({ [name]: value });
        return { ok: true, reason: `${formatLabel(name)} updated.` };
    }

    async matchTogglePlayerHighlight(playerId: number): Promise<{ ok: boolean; reason: string }> {
        if (!this.game) {
            return { ok: false, reason: 'Match runtime unavailable.' };
        }
        this.game.toggleHomePlayerHighlight(playerId);
        return { ok: true, reason: 'Player highlight toggled.' };
    }

    async matchTogglePlayerDetails(playerId: number): Promise<{ ok: boolean; reason: string }> {
        if (!this.game) {
            return { ok: false, reason: 'Match runtime unavailable.' };
        }
        this.game.toggleHomePlayerDetails(playerId);
        return { ok: true, reason: 'Player details toggled.' };
    }

    async matchMakeSubstitution(outPlayerId: number, inPlayerId: number): Promise<{ ok: boolean; reason: string }> {
        if (!this.game) {
            return { ok: false, reason: 'Match runtime unavailable.' };
        }
        return this.game.makeManagedSubstitution(outPlayerId, inPlayerId);
    }

    private rebuildManagerSnapshot(): void {
        if (!this.manager) {
            this.snapshot = {
                ...this.snapshot,
                manager: null
            };
            this.notify();
            return;
        }
        const state = this.manager.getState();
        const summary = this.manager.getHomeSummary();
        const squadState = this.manager.getSquadState();
        const inboxState = this.manager.getInboxState();
        const staffState = this.manager.getStaffState();
        const transferState = this.manager.getTransferState();
        const managedClubId = state.managedClubState?.clubId ?? MANAGED_CLUB_ID;
        const upcomingManagedFixtures = (state.seasonState?.fixtures ?? [])
            .filter((fixture: any) =>
                !fixture.played
                && fixture.scheduledAt.slice(0, 10) >= state.currentDateIso
                && (fixture.homeClubId === managedClubId || fixture.awayClubId === managedClubId))
            .sort((a: any, b: any) => {
                if (a.matchday !== b.matchday) {
                    return a.matchday - b.matchday;
                }
                return String(a.scheduledAt).localeCompare(String(b.scheduledAt));
            });
        const nextManagedMatchday = upcomingManagedFixtures[0]?.matchday;
        const activeInjuryByPlayer = new Map<number, { type: 'impact' | 'muscle' | 'knock'; severity: number; outUntilMatchday: number; remainingMatches: number }>();
        for (const record of (state.seasonState?.injuries ?? [])) {
            if (record.clubId !== managedClubId) {
                continue;
            }
            if (nextManagedMatchday !== undefined && record.outUntilMatchday < nextManagedMatchday) {
                continue;
            }
            const remainingMatches = upcomingManagedFixtures.filter((fixture: any) => fixture.matchday <= record.outUntilMatchday).length;
            activeInjuryByPlayer.set(record.playerId, {
                type: record.type,
                severity: Math.max(1, Math.round(Number(record.severity ?? 1))),
                outUntilMatchday: Math.max(1, Math.round(Number(record.outUntilMatchday ?? nextManagedMatchday ?? 1))),
                remainingMatches
            });
        }
        const activeSuspensionByPlayer = new Map<number, { suspendedUntilMatchday: number; remainingMatches: number }>();
        for (const record of (state.seasonState?.disciplinary ?? [])) {
            if (record.clubId !== managedClubId || !Number.isFinite(record.suspendedUntilMatchday)) {
                continue;
            }
            if (nextManagedMatchday !== undefined && Number(record.suspendedUntilMatchday) < nextManagedMatchday) {
                continue;
            }
            const suspendedUntilMatchday = Math.max(1, Math.round(Number(record.suspendedUntilMatchday)));
            const remainingMatches = upcomingManagedFixtures.filter((fixture: any) => fixture.matchday <= suspendedUntilMatchday).length;
            activeSuspensionByPlayer.set(record.playerId, {
                suspendedUntilMatchday,
                remainingMatches
            });
        }

        const players = squadState.players.map((player: any) => {
            const rating = computePlayerOverallRating(player);
            const assignment = state.managedClubState?.trainingAssignments?.[player.id] ?? { plan: 'balanced', intensity: 2 };
            const morale = player.attributes?.morale ?? 50;
            const listedPreference = state.managedClubState?.playerSalePreferences?.[player.id] ?? null;
            const availability = (squadState.availability?.[player.id] ?? 'available');
            return {
                id: player.id,
                name: player.name,
                role: player.role,
                readiness: Math.round(player.fitness ?? 0),
                availability,
                ...(availability === 'injured' ? { injuryStatus: activeInjuryByPlayer.get(player.id) } : {}),
                ...(availability === 'suspended' ? { suspensionStatus: activeSuspensionByPlayer.get(player.id) } : {}),
                rating,
                training: `${formatLabel(String(assignment.plan ?? 'balanced'))} I${assignment.intensity ?? 2}`,
                selected: squadState.selection.startingXI.includes(player.id),
                morale: morale >= 70 ? 'Good' : morale >= 50 ? 'Okay' : 'Poor',
                recommendation: 'Review',
                avatar: getManagerAvatarSvg(player),
                keySkills: {
                    pace: Math.round(Number(player.attributes?.pace ?? 50)),
                    passing: Math.round(Number(player.attributes?.passing ?? 50)),
                    tackling: Math.round(Number(player.attributes?.tackling ?? 50)),
                    finishing: Math.round(Number(player.attributes?.shotPower ?? 50))
                },
                listedForTransfer: Boolean(state.managedClubState?.transferListedPlayerIds?.includes(player.id)),
                listedTargetPrice: listedPreference ? Number(listedPreference.targetPrice ?? 0) : null,
                attributes: {
                    pace: Math.round(Number(player.attributes?.pace ?? 50)),
                    ballControl: Math.round(Number(player.attributes?.ballControl ?? 50)),
                    passing: Math.round(Number(player.attributes?.passing ?? 50)),
                    tackling: Math.round(Number(player.attributes?.tackling ?? 50)),
                    endurance: Math.round(Number(player.attributes?.endurance ?? 50)),
                    morale: Math.round(Number(player.attributes?.morale ?? 50)),
                    positioning: Math.round(Number(player.attributes?.positioning ?? 50)),
                    shotPower: Math.round(Number(player.attributes?.shotPower ?? 50)),
                    jumping: Math.round(Number(player.attributes?.jumping ?? 50)),
                    reactions: Math.round(Number(player.attributes?.reactions ?? 50)),
                    composure: Math.round(Number(player.attributes?.composure ?? 50)),
                    goalkeeping: Math.round(Number(player.attributes?.goalkeeping ?? (player.role === 'GK' ? 68 : 30)))
                }
            };
        });

        const managedFixtureId = state.todayManagedFixture?.id ?? null;
        const blockSameMatchdayResults = Boolean(state.readyToPlayManagedFixture && !state.lastManagedMatchResult);
        const blockedMatchday = state.todayManagedFixture?.matchday;
        const fixtures = (this.manager.getFixturesForScreen() ?? []).map((fixture: any) => {
            const hideScoreline = blockSameMatchdayResults
                && fixture.matchday === blockedMatchday
                && fixture.id !== managedFixtureId;
            return {
                matchday: fixture.matchday,
                date: fixture.scheduledAt.slice(0, 10),
                home: state.seasonState?.clubs?.find((club: any) => club.id === fixture.homeClubId)?.name ?? fixture.homeClubId,
                away: state.seasonState?.clubs?.find((club: any) => club.id === fixture.awayClubId)?.name ?? fixture.awayClubId,
                homeScore: hideScoreline ? undefined : fixture.result?.homeGoals,
                awayScore: hideScoreline ? undefined : fixture.result?.awayGoals,
                status: (hideScoreline ? 'scheduled' : (fixture.played ? 'completed' : 'scheduled')) as 'completed' | 'scheduled'
            };
        });

        const introSummary = this.manager.getManagedMatchIntroSummary();
        const nextManagedFixture = upcomingManagedFixtures[0] ?? null;
        const previewIntroSummary = introSummary ?? (nextManagedFixture ? {
            fixtureId: String(nextManagedFixture.id ?? ''),
            homeClubName: this.manager.getClubName(String(nextManagedFixture.homeClubId ?? '')),
            awayClubName: this.manager.getClubName(String(nextManagedFixture.awayClubId ?? '')),
            date: formatManagerDate(String(nextManagedFixture.scheduledAt ?? '').slice(0, 10)),
            location: `${this.manager.getClubName(String(nextManagedFixture.homeClubId ?? ''))} Ground`,
            competition: formatLabel(String(nextManagedFixture.competitionId ?? 'league'))
        } : null);
        const teamSelection = this.manager.getTeamSelectionViewModel();
        const byId = new Map<number, any>(squadState.players.map((player: any) => [player.id, player]));

        const mapLeagueRow = (row: any) => ({
            position: row.pos,
            club: row.clubName,
            played: row.played,
            won: row.wins,
            drawn: row.draws,
            lost: row.losses,
            gf: row.gf,
            ga: row.ga,
            gd: row.gd,
            points: row.points,
            form: row.form
        });
        const fullLeagueTable = summary.table.map(mapLeagueRow);
        const managedRowIndex = summary.table.findIndex((row: any) => row.clubId === MANAGED_CLUB_ID);
        const aroundStart = managedRowIndex >= 0 ? Math.max(0, managedRowIndex - 3) : 0;
        const aroundEnd = managedRowIndex >= 0 ? Math.min(summary.table.length, managedRowIndex + 4) : Math.min(summary.table.length, 7);
        const aroundLeagueTable = summary.table.slice(aroundStart, aroundEnd).map(mapLeagueRow);

        const mapIntroSlot = (slot: any) => {
            if (slot.playerId === null) {
                return {
                    slotIndex: slot.slotIndex,
                    role: slot.role,
                    playerId: null,
                    playerName: 'Unassigned',
                    playerRole: slot.role,
                    availability: 'available',
                    rating: 0,
                    readiness: 0,
                    avatar: undefined
                };
            }
            const player = byId.get(slot.playerId);
            const availability = player ? (squadState.availability[player.id] ?? 'available') : 'available';
            return {
                slotIndex: slot.slotIndex,
                role: slot.role,
                playerId: slot.playerId,
                playerName: player?.name ?? 'Unknown',
                playerRole: player?.role ?? slot.role,
                availability,
                rating: player ? computePlayerOverallRating(player) : 0,
                readiness: player?.fitness ?? 0,
                avatar: player ? getManagerAvatarSvg(player) : undefined
            };
        };
        const teamSelectionVm = {
            formationName: teamSelection.formationName,
            formationOptions: teamSelection.formationOptions.slice(),
            validation: {
                ok: teamSelection.validation.ok,
                errors: [...(teamSelection.validation.errors ?? [])]
            },
            xiSlots: teamSelection.xiSlots.map(mapIntroSlot),
            benchSlots: teamSelection.benchSlots.map(mapIntroSlot),
            outSlots: teamSelection.outPool.map((slot: any) => mapIntroSlot({ ...slot, role: 'OUT' }))
        };
        const scoutQuality = estimateScoutQuality(staffState.staff ?? []);
        const normalizeTransferTargetStatus = (value: unknown): ManagerVm['transfers']['targets'][number]['status'] => {
            const allowed: Array<ManagerVm['transfers']['targets'][number]['status']> = [
                'scouted',
                'scouting_in_progress',
                'cooldown',
                'shortlisted',
                'bid_prepared',
                'awaiting_club_response',
                'club_rejected',
                'club_accepted',
                'awaiting_player_response',
                'personal_terms_failed',
                'deal_agreed',
                'signed',
                'offer_received',
                'offer_countered',
                'withdrawn',
                'expired'
            ];
            const status = String(value ?? 'scouted') as ManagerVm['transfers']['targets'][number]['status'];
            return allowed.includes(status) ? status : 'scouted';
        };
        const mapTransferTargetVm = (target: any) => ({
            id: target.id,
            name: target.name,
            position: target.role,
            rating: target.rating,
            value: Number(target.askingPrice ?? target.estimatedMarketValue ?? 0),
            status: normalizeTransferTargetStatus(target.status),
            clubName: target.currentClubName,
            listingStatus: target.listingStatus,
            listedBy: target.listedBy,
            listingReason: target.listingReason,
            wageExpectation: target.wageExpectation,
            cooldownDaysRemaining: Number.isFinite(target.cooldownDaysRemaining) ? Math.max(0, Math.round(target.cooldownDaysRemaining)) : 0,
            scoutConfidence: Number(target.scoutConfidence ?? 50),
            attributes: mapTransferAttributes(target),
            scoutUncertaintyByAttribute: mapTransferScoutUncertainty(target, scoutQuality),
            avatar: getManagerAvatarSvg({
                id: Number(target.playerId ?? target.id ?? 0),
                name: String(target.name ?? 'Player'),
                role: String(target.role ?? 'CM'),
                appearance: target.appearance
            } as any)
        });
        const transferTargetById = new Map((transferState.market ?? []).map((target: any) => [String(target.id), target]));
        const managedSelection = state.managedClubState?.matchdaySelection ?? { startingXI: [], bench: [], out: [] };
        const buildFocusPlayerContext = (playerId: number | null | undefined) => {
            if (!Number.isFinite(playerId)) {
                return null;
            }
            const resolvedPlayerId = Number(playerId);
            const player = squadState.players.find((entry: any) => Number(entry.id) === resolvedPlayerId);
            if (!player) {
                return null;
            }
            const rolePeers = squadState.players.filter((entry: any) => String(entry.role ?? '') === String(player.role ?? ''));
            const peerRatings = rolePeers
                .map((entry: any) => ({
                    playerId: Number(entry.id),
                    rating: computeRoleOverallRating(String(entry.role ?? ''), entry.attributes ?? {})
                }))
                .sort((a: { playerId: number; rating: number }, b: { playerId: number; rating: number }) => b.rating - a.rating || a.playerId - b.playerId);
            const roleRankIndex = peerRatings.findIndex((entry: { playerId: number; rating: number }) => entry.playerId === resolvedPlayerId);
            const stats = state.managedClubState?.seasonStatsByPlayer?.[resolvedPlayerId] ?? { starts: 0, benchAppearances: 0, goals: 0, assists: 0 };
            const selectionHistory = state.managedClubState?.selectionHistory?.[resolvedPlayerId] ?? null;
            const roleAttributes = rankRoleRelevantAttributes(String(player.role ?? ''), player.attributes ?? {})
                .slice(0, 4)
                .map((entry) => ({
                    key: entry.key,
                    label: formatAttributeLabel(entry.key),
                    value: Math.round(Number(entry.value ?? 50))
                }));
            const selectionStatus = managedSelection.startingXI.includes(resolvedPlayerId)
                ? 'In starting XI'
                : managedSelection.bench.includes(resolvedPlayerId)
                    ? 'On the bench'
                    : 'Out of squad';
            return {
                playerId: resolvedPlayerId,
                name: String(player.name ?? 'Unknown Player'),
                role: String(player.role ?? 'N/A'),
                selectionStatus,
                roleOverall: computeRoleOverallRating(String(player.role ?? ''), player.attributes ?? {}),
                roleRank: roleRankIndex >= 0 ? roleRankIndex + 1 : 1,
                roleCount: Math.max(1, rolePeers.length),
                transferListed: Boolean(state.managedClubState?.transferListedPlayerIds?.includes(resolvedPlayerId)),
                stats: {
                    starts: Number(stats.starts ?? 0),
                    benchAppearances: Number(stats.benchAppearances ?? 0),
                    goals: Number(stats.goals ?? 0),
                    assists: Number(stats.assists ?? 0),
                    lastSelectedMatchday: Number.isFinite(selectionHistory?.lastSelectedMatchday)
                        ? Number(selectionHistory.lastSelectedMatchday)
                        : null
                },
                keyAttributes: roleAttributes
            };
        };
        const lastManagedFixture = state.lastManagedMatchResult
            ? (state.seasonState?.fixtures ?? []).find((fixture: any) => fixture.id === state.lastManagedMatchResult?.fixtureId)
            : null;

        const managerVm: ManagerVm = {
            teamName: state.seasonState?.clubs?.find((club: any) => club.id === MANAGED_CLUB_ID)?.name ?? 'Managed United',
            managerName: 'You',
            home: {
                leagueTable: aroundLeagueTable,
                fullLeagueTable
            },
            squad: {
                players,
                selectedPlayerId: this.selectedSquadPlayerId
            },
            fixtures,
            inbox: inboxState.items.map((item: any) => ({
                id: item.id,
                sender: item.senderName ?? item.sender ?? 'Staff',
                subject: item.subject,
                preview: item.previewText ?? item.prompt ?? '',
                prompt: item.prompt ?? '',
                date: String(item.createdDateIso ?? ''),
                status: item.status,
                read: item.status !== 'unread',
                hasActions: item.requiresAction,
                options: (item.options ?? []).map((option: any) => ({
                    id: String(option.id),
                    text: String(option.text),
                    actionForm: option.actionForm
                        ? {
                            title: String(option.actionForm.title ?? ''),
                            submitLabel: String(option.actionForm.submitLabel ?? 'Submit'),
                            fields: Array.isArray(option.actionForm.fields)
                                ? option.actionForm.fields.map((field: any) => ({
                                    id: (() => {
                                        const fieldId = String(field.id ?? '');
                                        if (fieldId === 'wageOffer') {
                                            return 'wageOffer';
                                        }
                                        if (fieldId === 'listingPrice') {
                                            return 'listingPrice';
                                        }
                                        return 'bidAmount';
                                    })(),
                                    label: String(field.label ?? ''),
                                    kind: 'money',
                                    min: Number(field.min ?? 0),
                                    max: Number(field.max ?? 0),
                                    step: Number(field.step ?? 1),
                                    defaultValue: Number(field.defaultValue ?? 0),
                                    required: Boolean(field.required)
                                }))
                                : []
                        }
                        : null
                })),
                transferFocusTargetId: item.transferFocusTargetId ?? null,
                transferThreadStage: item.transferThreadStage ?? null,
                transferThreadVersion: item.transferThreadVersion ?? null,
                transferPlayerSummary: (() => {
                    if (item.transferScoutReport) {
                        return null;
                    }
                    const targetId = item.transferFocusTargetId ? String(item.transferFocusTargetId) : null;
                    if (!targetId) {
                        return null;
                    }
                    const target = transferTargetById.get(targetId);
                    if (!target?.attributes) {
                        return null;
                    }
                    return {
                        position: String(target.role ?? 'N/A'),
                        attributes: {
                            pace: Math.round(Number(target.attributes.pace ?? 50)),
                            passing: Math.round(Number(target.attributes.passing ?? 50)),
                            tackling: Math.round(Number(target.attributes.tackling ?? 50)),
                            composure: Math.round(Number(target.attributes.composure ?? 50))
                        }
                    };
                })(),
                transferScoutReport: item.transferScoutReport ?? null,
                transferMarketEvent: item.transferMarketEvent ?? null,
                contextSections: (item.contextSections ?? []).map((section: any) => ({
                    title: String(section.title ?? ''),
                    lines: Array.isArray(section.lines) ? section.lines.map((line: any) => String(line ?? '')) : []
                })),
                focusPlayerContext: (() => {
                    const focusId = Array.isArray(item.focusPlayerIds) && item.focusPlayerIds.length > 0
                        ? Number(item.focusPlayerIds[0])
                        : null;
                    return buildFocusPlayerContext(focusId);
                })(),
                avatar: undefined
            })),
            staff: (staffState.staff ?? []).map((staff: any) => ({
                id: staff.id,
                name: staff.name,
                role: staff.role,
                rating: staff.quality,
                wage: Math.round(staff.weeklyWage / 1000),
                contract: 12
            })),
            transfers: {
                targets: transferState.market.map((target: any) => mapTransferTargetVm(target)),
                shortlist: transferState.shortlist.map((target: any) => mapTransferTargetVm(target)),
                listedPlayers: (transferState.listedPlayers ?? []).map((entry: any) => {
                    const player = entry?.player ?? {};
                    const preference = entry?.preference ?? {};
                    return {
                        playerId: Number(player.id ?? 0),
                        name: String(player.name ?? 'Unknown Player'),
                        position: String(player.role ?? 'N/A'),
                        rating: computePlayerOverallRating(player),
                        value: Number(preference.minimumAcceptableBid ?? preference.targetPrice ?? 0),
                        listedTargetPrice: Number(preference.targetPrice ?? 0),
                        avatar: getManagerAvatarSvg(player),
                        attributes: mapTransferAttributes({
                            position: player.role,
                            rating: computePlayerOverallRating(player),
                            attributes: player.attributes
                        })
                    };
                }),
                focusTargetId: this.focusedTransferTargetId,
                focusVersion: this.focusedTransferVersion,
                incomingOffers: (transferState.incomingOffers ?? []).map((offer: any) => ({
                    id: String(offer.id),
                    playerId: typeof offer.playerId === 'number' ? offer.playerId : null,
                    playerName: String(offer.playerName ?? 'Unknown Player'),
                    role: String(offer.role ?? 'N/A'),
                    offeringClubName: String(offer.counterpartyClubName ?? 'Unknown Club'),
                    offeredFee: Number(offer.bidAmount ?? 0),
                    wageOffer: Number(offer.wageOffer ?? 0),
                    requestedFee: offer.requestedFee === null || offer.requestedFee === undefined ? null : Number(offer.requestedFee),
                    status: String(offer.status ?? 'offer_received'),
                    responseMessage: String(offer.responseMessage ?? ''),
                    daysRemaining: Number(offer.daysRemaining ?? 0)
                })),
                negotiations: (transferState.negotiations ?? []).map((negotiation: any) => ({
                    id: String(negotiation.id),
                    targetId: negotiation.targetId === null || negotiation.targetId === undefined ? null : String(negotiation.targetId),
                    playerName: String(negotiation.playerName ?? 'Unknown Player'),
                    role: String(negotiation.role ?? 'N/A'),
                    counterpartyClubName: String(negotiation.counterpartyClubName ?? 'Unknown Club'),
                    status: String(negotiation.status ?? 'awaiting_club_response'),
                    bidAmount: Number(negotiation.bidAmount ?? 0),
                    wageOffer: Number(negotiation.wageOffer ?? 0),
                    requestedFee: negotiation.requestedFee === null || negotiation.requestedFee === undefined ? null : Number(negotiation.requestedFee),
                    responseMessage: String(negotiation.responseMessage ?? ''),
                    daysRemaining: Number(negotiation.daysRemaining ?? 0)
                }))
            },
            competitions: {
                leagueTable: fullLeagueTable
            },
            teamSelection: teamSelectionVm,
            layout: {
                dateLabel: `Date: ${summary.date}`,
                nextLabel: `Next: ${summary.nextFixture}`,
                financeLabel: `Budget: £${Math.round(summary.finances.budget).toLocaleString()}`,
                wagesPerWeek: Math.round(summary.finances.wagesPerWeek ?? 0),
                ctaLabel: `Action: ${summary.ready ? 'Play Match' : 'Next Day'}`,
                message: this.message || summary.message,
                inboxBadgeCount: this.manager.getInboxBadgeCount()
            },
            teamSelectionContext: previewIntroSummary ? {
                fixture: `${previewIntroSummary.homeClubName} vs ${previewIntroSummary.awayClubName}`,
                date: previewIntroSummary.date,
                location: previewIntroSummary.location,
                competition: previewIntroSummary.competition
            } : null,
            intro: introSummary ? {
                fixture: `${introSummary.homeClubName} vs ${introSummary.awayClubName}`,
                date: introSummary.date,
                location: introSummary.location,
                competition: introSummary.competition,
                validity: teamSelection.validation.ok ? 'Selection ready' : (teamSelection.validation.errors?.[0] ?? 'Selection invalid'),
                selection: teamSelectionVm
            } : null,
            result: state.lastManagedMatchResult ? {
                score: `${state.lastManagedMatchResult.homeGoals} - ${state.lastManagedMatchResult.awayGoals}`,
                fixture: `${state.lastManagedMatchResult.homeClubName} vs ${state.lastManagedMatchResult.awayClubName}`,
                scorers: state.lastManagedMatchResult.goalEvents.map((event: any) => `${event.minute}' ${event.scorerName}`),
                otherResults: (state.seasonState?.fixtures ?? [])
                    .filter((fixture: any) =>
                        fixture.matchday === lastManagedFixture?.matchday
                        && fixture.id !== state.lastManagedMatchResult?.fixtureId
                        && fixture.played
                    )
                    .map((fixture: any) => {
                        const homeName = state.seasonState?.clubs?.find((club: any) => club.id === fixture.homeClubId)?.name ?? fixture.homeClubId;
                        const awayName = state.seasonState?.clubs?.find((club: any) => club.id === fixture.awayClubId)?.name ?? fixture.awayClubId;
                        const homeGoals = Number.isFinite(fixture.result?.homeGoals) ? fixture.result.homeGoals : 0;
                        const awayGoals = Number.isFinite(fixture.result?.awayGoals) ? fixture.result.awayGoals : 0;
                        return `${homeName} ${homeGoals}-${awayGoals} ${awayName}`;
                    })
            } : null,
            daySummary: state.pendingDaySummary ? {
                headline: state.pendingDaySummary.headline,
                date: state.pendingDaySummary.date,
                progress: 'Step 1 of 1',
                highlights: state.pendingDaySummary.highlights,
                fixtures: state.pendingDaySummary.simulatedFixtures.map((fixture: any) => `${fixture.homeClubName} ${fixture.scoreline} ${fixture.awayClubName}`)
            } : null
        };

        this.snapshot = {
            ...this.snapshot,
            manager: managerVm,
            splash: {
                ...this.snapshot.splash,
                canLoadCampaign: this.campaignSaveAvailable
            }
        };
        this.notify();
    }

}

const runtime = new ShellRuntime();

document.addEventListener('visibilitychange', () => {
    (runtime as any).syncGameViewMode();
});

window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    const alreadyRetried = window.sessionStorage.getItem(VITE_PRELOAD_RETRY_KEY) === '1';
    if (alreadyRetried) {
        window.sessionStorage.removeItem(VITE_PRELOAD_RETRY_KEY);
        return;
    }
    window.sessionStorage.setItem(VITE_PRELOAD_RETRY_KEY, '1');
    window.location.reload();
});

async function init(): Promise<void> {
    const restoredPath = readRestorePathFromSession();
    if (restoredPath) {
        window.history.replaceState({}, '', restoredPath);
    }
    await (runtime as any).refreshCampaignSaveState();
    if (loading) {
        loading.style.display = 'none';
    }
    if (appRoot) {
        createRoot(appRoot).render(createElement(AppHost, { runtime }));
    }
}

void init();
